import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { HomeIcon, ExitIcon, ChatBubbleIcon, ResetIcon } from '@radix-ui/react-icons';
import { MoreHorizontal } from 'lucide-react';
import type { RagChatType } from '../../store/chatModeSlice';
import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../features/auth/authSlice';
import { useTheme } from '../Theme/ThemeProvider';
import { FeedbackDialog } from './FeedbackDialog';
import { RootState } from '../../store/store';
import { setRagChatType, clearaskChatHistory, clearRagHistory, cancelTranscribingMessages } from '../../store/chatModeSlice';
import { clearTranslationData, clearUploadedFiles } from '../../store/chatSlice';
import { setVoiceModeActive, setVoiceModeListening, setVoiceModePaused, setVoiceModeRestarting, setShowInactivityWarning, setInactivityCountdown, setDisconnectedByInactivity } from '../../store/voiceModeSlice';
import { voiceModeService } from '../../utils/voice/voiceMode.service';
import logger from '../../utils/logger/logger';
import { getRagMenuItems } from '../../utils/rag/ragConfig';
import { SearchInput } from '../common/SearchInput';
// import html2canvas from 'html2canvas'; // Temporarily disabled - will be used for "save chat" feature

interface MenuProps {
  accentShadow?: string;
  currentPage?: 'main' | 'chat';
  onClearChat?: () => void;
  width?: number;
  onWidthChange?: (width: number) => void;
  onShowWhatsNew?: () => void;
  onPromptLibraryClick?: () => void;
  isPromptLibraryOpen?: boolean;
  deviceType?: 'mobile' | 'tablet' | 'desktop';
}

// Side Menu contains settings like light vs. dark mode, accent colors, logout, RAG pages
export function Menu({ accentShadow, currentPage = 'main', onClearChat, width: externalWidth, onWidthChange, onShowWhatsNew, onPromptLibraryClick, isPromptLibraryOpen = false, deviceType = 'desktop' }: MenuProps) {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const { accent } = useTheme();
  const chatModeState = useSelector((state: RootState) => state.chatMode);
  const { isActive: isVoiceModeActive } = useSelector((state: RootState) => state.voiceMode);

  const stopVoiceMode = () => {
    if (isVoiceModeActive) {
      dispatch(cancelTranscribingMessages());
      voiceModeService.disconnect();
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false));
      dispatch(setVoiceModePaused(false));
      dispatch(setVoiceModeRestarting(false));
      dispatch(setShowInactivityWarning(false));
      dispatch(setInactivityCountdown(0));
      dispatch(setDisconnectedByInactivity(false));
    }
  };
  
  const handleRagChatSelect = (chatType: RagChatType) => {
    // Only block re-clicking the currently active RAG while streaming — that would call
    // onClearChat and erase the in-progress conversation. Switching to a *different* RAG
    // is safe because every REST RAG has its own permanently-mounted hook instance; the
    // ongoing stream continues in the background and the user can return to see it.
    if (chatModeState.isStreaming && chatModeState.ragChatType === chatType) return;
    // Close prompt library if open
    if (isPromptLibraryOpen && onPromptLibraryClick) onPromptLibraryClick();
    const isAlreadyActive = chatModeState.ragChatType === chatType;

    if (isAlreadyActive && currentPage === 'main') {
      // Already on the RAG's home page — deselect it and show the RAG selector
      // Suppress All Apps hover briefly so it doesn't open when the button shifts up under the cursor
      suppressAllAppsHoverRef.current = true;
      setTimeout(() => { suppressAllAppsHoverRef.current = false; }, 400);
      dispatch(setRagChatType(null));
      dispatch(clearRagHistory());
      dispatch(clearTranslationData());
      dispatch(clearUploadedFiles());
      return;
    }

    dispatch(setRagChatType(chatType));
    // Clear voice history when switching RAG modes
    dispatch(clearRagHistory());
    // Clear translation mode state when switching to a RAG
    dispatch(clearTranslationData());
    // Clear uploaded files when switching to any RAG mode
    dispatch(clearUploadedFiles());
    // On chat page, clicking the already-active RAG navigates back to the RAG's home page
    if (onClearChat && isAlreadyActive && currentPage === 'chat') {
      onClearChat();
    }
  };

  // RAG items configuration — derived from central ragConfig registry
  const ragItemsConfig = getRagMenuItems();

  // Helper to get the config for the currently active RAG
  const getActiveRagConfig = () => ragItemsConfig.find(r => r.key === chatModeState.ragChatType) || null;

  // Collapsible width state with localStorage persistence
  const minWidth = 60; // px, threshold for collapse
  const maxWidth = 180; // px, fully open
  const initialWidth = 90; // px, just bigger than content
  
  // Load saved width from localStorage or use initial width
  const getSavedWidth = () => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('menu-width');
      if (saved) {
        const parsedWidth = parseInt(saved, 10);
        // Allow 0 explicitly to represent a collapsed menu.
        if (parsedWidth === 0) return 0;
        // Ensure saved width is within valid bounds (non-collapsed state)
        if (parsedWidth >= minWidth && parsedWidth <= maxWidth) {
          return parsedWidth;
        }
      }
    }
    return initialWidth;
  };
  
  // Resolve saved width once for initial states
  const savedWidth = getSavedWidth();
  // Use external width if provided, otherwise use internal state
  const [internalWidth, setInternalWidth] = useState(savedWidth);
  const width = externalWidth !== undefined ? externalWidth : internalWidth;
  // Collapsed state derives from saved width (0 means collapsed)
  const [collapsed, setCollapsed] = useState(savedWidth === 0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showAllApps, setShowAllApps] = useState(false);
  const [allAppsSearch, setAllAppsSearch] = useState('');
  const [allAppsPopupPos, setAllAppsPopupPos] = useState<{ top: number; left: number } | null>(null);
  const allAppsRef = useRef<HTMLLIElement>(null);
  const allAppsLockedRef = useRef(false); // true when user has clicked to pin the popup open
  const allAppsCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressAllAppsHoverRef = useRef(false); // true briefly after deselecting a RAG to prevent hover-open
  const dragging = useRef(false);
  // Track the last width we attempted to persist (helps guarantee persistence on very fast drags)
  const lastWidthRef = useRef<number>(savedWidth);

  // Helper function to save width to localStorage and update state
  const saveWidthToStorage = (newWidth: number) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('menu-width', newWidth.toString());
    }
    
    // Update internal state if no external width is provided
    if (externalWidth === undefined) {
      setInternalWidth(newWidth);
    }
    
    // Always notify parent if callback is provided
    if (onWidthChange) {
      onWidthChange(newWidth);
    }
  };

  // Initialize parent with saved width on mount
  useEffect(() => {
    if (onWidthChange && externalWidth === undefined) {
      const savedWidth = getSavedWidth();
      onWidthChange(savedWidth);
    }
  }, [onWidthChange, externalWidth]);

  // Map current accent to match the main content colors in dark mode
  // These should be the same colors used in the main content area for consistency
  const getLightAccentTextColor = (accentColor: string) => {
    if (accentColor === 'purple') return '#a78bfa'; // purple-400 - matches dark mode main content
    if (accentColor === 'blue') return '#60a5fa'; // blue-400 - matches dark mode main content  
    if (accentColor === 'green') return '#34d399'; // emerald-400 - matches dark mode main content
    return '#e5e7eb'; // gray-200 for none themes
  };

  // Get gradient color based on accent theme
  const getGradientColor = (accentColor: string) => {
    if (accentColor === 'purple') return '139, 92, 246'; // purple-500 RGB
    if (accentColor === 'blue') return '59, 130, 246'; // blue-500 RGB
    if (accentColor === 'green') return '16, 185, 129'; // emerald-500 RGB
    if (accentColor === 'gold') return '245, 158, 11'; // amber-500 RGB
    return '209, 4, 17'; // red RGB as default for 'none' themes
  };

  // Get actual hex color for accent (for use in gradients and borders)
  const getAccentHexColor = (accentColor: string) => {
    if (accentColor === 'purple') return '#8b5cf6'; // purple-500
    if (accentColor === 'blue') return '#3b82f6'; // blue-500
    if (accentColor === 'green') return '#10b981'; // emerald-500
    return '#e5e7eb'; // gray-200 for none themes
  };

  const textColor = getLightAccentTextColor(accent);
  const gradientColorRgb = getGradientColor(accent);
  const accentColor = getAccentHexColor(accent);

  // Debug logging for Everyday Insights border issue
  useEffect(() => {
    if (chatModeState.mode === '3M Knowledge') {
      logger.log('[Menu] 3M Knowledge mode active, ragChatType:', chatModeState.ragChatType);
      logger.log('[Menu] Everyday Insights selected?', chatModeState.ragChatType === 'Everyday Insights');
      logger.log('[Menu] accent value:', accent);
      logger.log('[Menu] accentColor value:', accentColor);
      logger.log('[Menu] gradientColorRgb value:', gradientColorRgb);
      logger.log('[Menu] Template literal test:', `linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 20%, ${accent} 40%, ${accent} 60%, rgba(255,255,255,0.9) 80%, rgba(255,255,255,1) 100%)`);
    }
  }, [chatModeState.mode, chatModeState.ragChatType, accent, accentColor, gradientColorRgb]);

  // Reset All Apps search query whenever the popup closes
  useEffect(() => {
    if (!showAllApps) setAllAppsSearch('');
  }, [showAllApps]);

  // Close locked All Apps popup when clicking outside both the button and the portal popup
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!allAppsLockedRef.current) return;
      const target = e.target as Node;
      const buttonContains = allAppsRef.current?.contains(target);
      const popupEl = document.getElementById('all-apps-portal');
      const popupContains = popupEl?.contains(target);
      if (!buttonContains && !popupContains) {
        allAppsLockedRef.current = false;
        setShowAllApps(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset popup position on window resize (browser zoom fires resize).
  // This ensures the next open re-measures the anchor rect at the new zoom level.
  useEffect(() => {
    const handleResize = () => {
      setShowAllApps(false);
      allAppsLockedRef.current = false;
      setAllAppsPopupPos(null);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mouse event handlers for drag-to-collapse
  const onMouseDown = () => {
    dragging.current = true;
    document.body.style.cursor = 'ew-resize';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!dragging.current) return;
    // Get the new width based on mouse X
    const newWidth = Math.max(0, Math.min(maxWidth, e.clientX)); // restrict max width
    if (newWidth <= minWidth) { // min width threshold
      setCollapsed(true);
      saveWidthToStorage(0); // persist collapsed state
      dragging.current = false;
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    } else {
      lastWidthRef.current = newWidth;
      saveWidthToStorage(newWidth);
    }
  };

  const onMouseUp = () => {
    if (dragging.current) {
      // Ensure final width is saved (in case last mousemove wasn't dispatched, e.g., very fast drag/release)
      if (!collapsed && lastWidthRef.current !== internalWidth && lastWidthRef.current > minWidth) {
        saveWidthToStorage(lastWidthRef.current);
      }
    }
    dragging.current = false;
    document.body.style.cursor = '';
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  // Handle logout functionality
  const handleLogout = async () => {
    try {
      // Clear Redux auth state
      dispatch(logout());
      
      // Perform MSAL logout (this will redirect to logout page)
      await instance.logoutRedirect({
        postLogoutRedirectUri: '/' // Will redirect to home page after logout
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Handle home navigation functionality with same animation as clear chat
  const handleHomeNavigation = async () => {
    // Close prompt library if open
    if (isPromptLibraryOpen && onPromptLibraryClick) onPromptLibraryClick();

    // Only navigate if we're on chat page
    if (!onClearChat || currentPage !== 'chat' || chatModeState.isStreaming) return;
    
    // Stop voice mode if active
    stopVoiceMode();
    
    // Clear voice history when navigating home
    dispatch(clearaskChatHistory());
    dispatch(clearRagHistory());
    // Clear uploaded files and translation data when navigating home
    dispatch(clearUploadedFiles());
    dispatch(clearTranslationData());
    
    // Find the main content area to fade (same logic as clear chat)
    const mainContent = document.querySelector('main') || 
                       document.querySelector('[data-content]') || 
                       document.querySelector('.main-content') ||
                       document.querySelector('#root > div > div:last-child'); // Fallback to likely main content
    
    if (mainContent) {
      const element = mainContent as HTMLElement;
      
      // Create an overlay specifically for the main content area for a fade in/out effect
      const contentRect = element.getBoundingClientRect();
      const overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.top = `${contentRect.top}px`;
      overlay.style.left = `${contentRect.left}px`;
      overlay.style.width = `${contentRect.width}px`;
      overlay.style.height = `${contentRect.height}px`;
      overlay.style.backgroundColor = 'var(--dark-main-background)';
      overlay.style.opacity = '0';
      overlay.style.transition = 'opacity 0.4s ease-in-out';
      overlay.style.zIndex = '1000';
      overlay.style.pointerEvents = 'none';
      
      // Add overlay to page
      document.body.appendChild(overlay);
      
      // Start fade in overlay over main content
      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
      });
      
      // Navigate when overlay covers content
      setTimeout(() => {
        onClearChat();
        
        // Start fading out overlay to reveal new content
        setTimeout(() => {
          overlay.style.opacity = '0';
          
          // Remove overlay after fade out completes
          setTimeout(() => {
            if (overlay.parentNode) {
              overlay.parentNode.removeChild(overlay);
            }
          }, 400);
        }, 100);
      }, 400);
    } else {
      // Fallback - just navigate immediately if main content not found
      onClearChat();
    }
  };

  // Handle clear chat functionality with smooth fade transition
  // TODO: Restore the complex screenshot-to-trash animation for "save chat" button later
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClearChat = async () => {
    if (!onClearChat || currentPage !== 'chat') return;

    // Stop voice mode if active
    stopVoiceMode();
    
    // Find the main content area to fade
    const mainContent = document.querySelector('main') || 
                       document.querySelector('[data-content]') || 
                       document.querySelector('.main-content') ||
                       document.querySelector('#root > div > div:last-child'); // Fallback to likely main content
    
    if (mainContent) {
      const element = mainContent as HTMLElement;
      
      // Create an overlay specifically for the main content area for a fade in/out effect
      const contentRect = element.getBoundingClientRect();
      const overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.top = `${contentRect.top}px`;
      overlay.style.left = `${contentRect.left}px`;
      overlay.style.width = `${contentRect.width}px`;
      overlay.style.height = `${contentRect.height}px`;
      overlay.style.backgroundColor = 'var(--dark-main-background)';
      overlay.style.opacity = '0';
      overlay.style.transition = 'opacity 0.4s ease-in-out';
      overlay.style.zIndex = '1000';
      overlay.style.pointerEvents = 'none';
      
      // Add overlay to page
      document.body.appendChild(overlay);
      
      // Start fade in overlay over main content
      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
      });
      
      // Navigate when overlay covers content
      setTimeout(() => {
        onClearChat();
        
        // Start fading out overlay to reveal new content
        setTimeout(() => {
          overlay.style.opacity = '0';
          
          // Remove overlay after fade out completes
          setTimeout(() => {
            if (overlay.parentNode) {
              overlay.parentNode.removeChild(overlay);
            }
          }, 400);
        }, 100);
      }, 400);
    } else {
      // Fallback - just navigate immediately if main content not found
      onClearChat();
    }

    /* 
    TODO: SAVED FOR FUTURE "SAVE CHAT" BUTTON:
    Complex screenshot-to-trash animation code below:
    
    try {
      // Find the main content area to capture (not the whole page)
      const contentElement = document.querySelector('main') || 
                            document.querySelector('[data-content]') || 
                            document.querySelector('.main-content') ||
                            document.body;
      
      // Find the exact location of the trash icon, specifically target the Clear Chat button with TrashIcon
      const trashButton = Array.from(document.querySelectorAll('button')).find(btn => {
        const hasTrashIcon = btn.querySelector('svg') && 
                            btn.textContent?.includes('Clear Chat');
        const isDisabled = btn.disabled; // if it's disabled, no animation
        return hasTrashIcon && !isDisabled;
      }) as HTMLElement;
      
      // Capture the content via html2canvas
      try {        
        const canvas = await html2canvas(contentElement as HTMLElement, {
          backgroundColor: null,
          scale: 0.8,
          useCORS: true,
          allowTaint: true
        });
        
        // Create an img element positioned at the content's location
        const img = document.createElement('img');
        img.src = canvas.toDataURL('image/png');
        img.style.position = 'fixed';
        img.style.zIndex = '9999';
        img.style.pointerEvents = 'none';
        img.style.border = '3px solid #000000'; // black border
        img.style.borderRadius = '8px';
        img.style.backgroundColor = '#ffffff'; // Solid background, could make it slightly transparent in the future
        
        // Position it at the content's current location
        const contentRect = contentElement.getBoundingClientRect();
        img.style.left = `${contentRect.left}px`;
        img.style.top = `${contentRect.top}px`;
        img.style.width = `${contentRect.width}px`;
        img.style.height = `${contentRect.height}px`;
        img.style.transition = 'transform 0.8s cubic-bezier(0.11, 0, 0.3, 0)'; // Slow start, faster middle, fastest end
        
        document.body.appendChild(img); // add to DOM
        
        // Navigate immediately to show homepage underneath
        onClearChat();
        
        // Get trash position with better fallback
        let trashRect;
        if (trashButton) {
          trashRect = trashButton.getBoundingClientRect();
        } else {
          // Fallback to left side of screen, bottom area, just in case something goes wrong with the image from html2canvas
          trashRect = { left: 12, top: window.innerHeight - 100, width: 24, height: 24 };
        }
        
        // the end animation target it the top right hand corner of the trash icon (looks weird if it goes to the bottom left or center)
        const trashCenterX = trashRect.left + trashRect.width; // Right edge of trash icon
        const trashCenterY = trashRect.top; // Top edge of trash icon
        
        // Calculate movement from content center to trash center
        const contentCenterX = contentRect.left + contentRect.width / 2; // starting x pos
        const contentCenterY = contentRect.top + contentRect.height / 2; // starting y pos
        const dx = trashCenterX - contentCenterX; // right side of trash
        const dy = trashCenterY - contentCenterY; // upper part of trash
        
        // Start the animation
        requestAnimationFrame(() => {
          img.style.transform = `translate(${dx}px, ${dy}px) scale(0.05)`;
        });
        
        // Cleanup after animation
        setTimeout(() => {
          if (img.parentNode) {
            img.remove();
          }
        }, 1600);        
      } catch (error) {
        console.error('html2canvas failed:', error);
        // Simple fallback where it just navigates without an animation
        onClearChat();
      }
      
    } catch (error) {
      console.error('Failed to create screenshot effect:', error);
      // Fallback... just navigate without animation
      onClearChat();
    }
    */
  };

  // Handle clear cache (dev only)
  const handleClearCache = () => {
    localStorage.clear();
    sessionStorage.clear();
    // Clear all cookies (theme and others are stored in both localStorage and cookies)
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict`;
    });
    if ('caches' in window) {
      caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
    }
    window.location.reload();
  };

  // Handle feedback dialog
  const handleFeedback = () => {
    setShowFeedback(true);
  };

  return (
    <>
      {/* Collapsible Menu */}
      {!collapsed && (
        <nav
          className="h-full overflow-visible shadow-md z-10 relative"
          style={{
            width: width,
            minWidth: minWidth,
            maxWidth: maxWidth,
            backgroundColor: 'var(--color-neutral-gray)',
            boxShadow: `0 2px 16px 0 ${accentShadow}`
          }}
        >
          {/* Drag handle at the right edge */}
          <div
            className="absolute top-0 right-0 h-full w-2 cursor-ew-resize z-30 bg-transparent transition"
            onMouseDown={onMouseDown}
            title="Drag to resize/collapse menu"
            style={{ 
              userSelect: 'none',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `linear-gradient(to bottom, rgb(32, 32, 36) 0%, rgba(${gradientColorRgb}, .5) 50%, rgb(32, 32, 36) 100%)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          />

          {/* Top Section with Home or RAG chat buttons */}
          <div className="pt-3 pb-3 pl-[13px] pr-[13px] h-full flex flex-col relative z-20">
            <ul className="space-y-2 flex-1 overflow-visible">
              {/* Show Home button for Ask Chat mode */}
              {chatModeState.mode === 'Ask Chat' && (
                <li 
                  className={`relative overflow-visible rounded-2xl pt-[8px] pb-[8px] flex flex-col items-center justify-center group ${chatModeState.isStreaming ? 'cursor-not-allowed opacity-50' : 'cursor-pointer transform hover:scale-105 hover:-translate-y-1 active:scale-95'}`}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    background: (currentPage === 'main' && chatModeState.ragChatType === null)
                      ? `radial-gradient(circle at center, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 100%)`
                      : 'radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)',
                    boxShadow: (currentPage === 'main' && chatModeState.ragChatType === null)
                      ? `0 0 0 3px ${accent}80, 
                         0 0 15px rgba(255,255,255,0.4),
                         0 4px 12px rgba(0,0,0,0.4), 
                         0 8px 20px rgba(0,0,0,0.3), 
                         inset 0 0 20px rgba(255,255,255,0.2),
                         inset 0 2px 6px rgba(255,255,255,0.4),
                         inset 0 -1px 2px rgba(0,0,0,0.3)`
                      : `0 1px 2px rgba(0,0,0,0.05), 
                         0 2px 4px rgba(0,0,0,0.03), 
                         inset 0 1px 0 rgba(255,255,255,0.1)`,
                    transition: 'transform 0.8s cubic-bezier(0.11, 0, 0.3, 0), all 0.2s ease'
                  }}
                >
                  {/* Gradient border pseudo-element */}
                  <div 
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                      background: (currentPage === 'main' && chatModeState.ragChatType === null)
                        ? `linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 20%, ${accentColor} 40%, ${accentColor} 60%, rgba(255,255,255,0.9) 80%, rgba(255,255,255,1) 100%)`
                        : 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.25) 80%, rgba(255,255,255,0.6) 100%)',
                      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      maskComposite: 'xor',
                      WebkitMaskComposite: 'xor',
                      padding: (currentPage === 'main' && chatModeState.ragChatType === null) ? '1.8px' : '1.5px',
                      zIndex: -1,
                    }}
                  />
                  <button onMouseDown={chatModeState.isStreaming ? undefined : handleHomeNavigation} disabled={chatModeState.isStreaming}
                      className="z-10 flex-col block rounded transition flex items-center group-hover:font-bold"
                      style={{ fontFamily: '3MCircularTT', color: textColor }}
                  >
                    <HomeIcon 
                      width={24} 
                      height={24} 
                      className="transition-all group-hover:font-bold"
                      style={{ 
                        color: (currentPage === 'main' && chatModeState.ragChatType === null) ? '#ffffff' : textColor,
                        filter: (currentPage === 'main' && chatModeState.ragChatType === null) ? `drop-shadow(0 0 8px rgba(255,255,255,0.9)) drop-shadow(0 0 15px ${accentColor})` : 'none',
                      }}
                    />
                    <p 
                      className="text-xs pt-[.5px] font-medium transition-all group-hover:font-bold" 
                      style={{ 
                        fontFamily: '3MCircularTT', 
                        color: (currentPage === 'main' && chatModeState.ragChatType === null) ? '#ffffff' : textColor,
                        fontWeight: (currentPage === 'main' && chatModeState.ragChatType === null) ? 700 : 500
                      }}
                    >
                      Home
                    </p>
                  </button>
                </li>
              )}
              
              {/* Show RAG chat buttons only for 3M Knowledge mode */}
              {chatModeState.mode === '3M Knowledge' && (
                <React.Fragment key={`rag-buttons-${chatModeState.ragChatType || 'none'}`}>
                  {/* Active RAG icon button — shown above All Apps when a RAG is selected */}
                  {chatModeState.ragChatType !== null && (() => {
                    const activeRag = getActiveRagConfig();
                    if (!activeRag) return null;
                    return (
                      <li
                        className={`relative overflow-visible rounded-2xl pt-[14px] pb-[14px] flex flex-col items-center justify-center group ${chatModeState.isStreaming ? 'cursor-not-allowed opacity-50' : 'cursor-pointer transform hover:scale-105 hover:-translate-y-1 active:scale-95'}`}


                        onClick={() => !chatModeState.isStreaming && handleRagChatSelect(chatModeState.ragChatType)}
                        title={`New Chat — ${activeRag.label}`}
                        style={{
                          backgroundColor: 'rgba(255,255,255,0.05)',
                          background: `radial-gradient(circle at center, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 100%)`,
                          boxShadow: `0 0 0 3px ${accent}80, 
                             0 0 15px rgba(255,255,255,0.4),
                             0 4px 12px rgba(0,0,0,0.4), 
                             0 8px 20px rgba(0,0,0,0.3), 
                             inset 0 0 20px rgba(255,255,255,0.2),
                             inset 0 2px 6px rgba(255,255,255,0.4),
                             inset 0 -1px 2px rgba(0,0,0,0.3)`,
                          transition: 'transform 0.8s cubic-bezier(0.11, 0, 0.3, 0), all 0.2s ease'
                        }}
                      >
                        {/* Gradient border */}
                        <div
                          className="absolute inset-0 rounded-2xl pointer-events-none"
                          style={{
                            background: `linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 20%, ${accentColor} 40%, ${accentColor} 60%, rgba(255,255,255,0.9) 80%, rgba(255,255,255,1) 100%)`,
                            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            maskComposite: 'xor',
                            WebkitMaskComposite: 'xor',
                            padding: '1.8px',
                            zIndex: -1,
                          }}
                        />
                        <div className="z-10 flex-col rounded transition flex items-center group-hover:font-bold w-full" style={{ fontFamily: '3MCircularTT', color: '#ffffff' }}>
                          {React.createElement(activeRag.iconComponent as any, {
                                size: 24,
                                className: 'transition-all group-hover:font-bold',
                                style: { color: '#ffffff', filter: `drop-shadow(0 0 8px rgba(255,255,255,0.9)) drop-shadow(0 0 15px ${accentColor})` }
                              })
                          }
                          <p
                            className="text-xs pt-[0.5px] px-1 font-bold transition-all group-hover:font-bold text-center w-full"
                            style={{ fontFamily: '3MCircularTT', color: '#ffffff', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis', wordBreak: 'normal' }}
                          >
                            {activeRag.shortLabel || activeRag.label}
                          </p>
                        </div>
                      </li>
                    );
                  })()}

                  {/* All Apps Button */}
                  <li
                    ref={allAppsRef}
                    className={`relative overflow-visible rounded-2xl pt-[14px] pb-[14px] flex flex-col items-center justify-center group cursor-pointer transform hover:scale-105 hover:-translate-y-1 active:scale-95`}
                    onMouseEnter={() => {
                      if (suppressAllAppsHoverRef.current) return;
                      if (allAppsCloseTimer.current) {
                        clearTimeout(allAppsCloseTimer.current);
                        allAppsCloseTimer.current = null;
                      }
                      // Only measure position on fresh open — not while popup is already visible
                      // (re-measuring after hover transform would shift the popup)
                      if (!showAllApps && allAppsRef.current) {
                        const rect = allAppsRef.current.getBoundingClientRect();
                        setAllAppsPopupPos({ top: rect.top, left: rect.right + 8 });
                      }
                      setShowAllApps(true);
                    }}
                    onMouseLeave={() => {
                      if (!allAppsLockedRef.current) {
                        allAppsCloseTimer.current = setTimeout(() => setShowAllApps(false), 250);
                      }
                    }}
                    onClick={() => {
                      allAppsLockedRef.current = true;
                    }}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      background: showAllApps || chatModeState.ragChatType !== null
                        ? `radial-gradient(circle at center, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 100%)`
                        : 'radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)',
                      boxShadow: showAllApps || chatModeState.ragChatType !== null
                        ? `0 0 0 4px ${accent}80, 
                           0 0 35px rgba(255,255,255,0.7),
                           0 4px 12px rgba(0,0,0,0.4), 
                           0 8px 20px rgba(0,0,0,0.3), 
                           inset 0 0 20px rgba(255,255,255,0.2),
                           inset 0 2px 6px rgba(255,255,255,0.4),
                           inset 0 -1px 2px rgba(0,0,0,0.3)`
                        : `0 1px 2px rgba(0,0,0,0.05), 
                           0 2px 4px rgba(0,0,0,0.03), 
                           inset 0 1px 0 rgba(255,255,255,0.1)`,
                      transition: 'transform 0.8s cubic-bezier(0.11, 0, 0.3, 0), all 0.2s ease'
                    }}
                  >
                    {/* Gradient border */}
                    <div
                      className="absolute inset-0 rounded-2xl pointer-events-none"
                      style={{
                        background: showAllApps || chatModeState.ragChatType !== null
                          ? `linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 20%, ${accentColor} 40%, ${accentColor} 60%, rgba(255,255,255,0.9) 80%, rgba(255,255,255,1) 100%)`
                          : 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.25) 80%, rgba(255,255,255,0.6) 100%)',
                        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        maskComposite: 'xor',
                        WebkitMaskComposite: 'xor',
                        padding: showAllApps || chatModeState.ragChatType !== null ? '2.5px' : '1.5px',
                        zIndex: -1,
                      }}
                    />
                    <div className="z-10 flex-col rounded transition flex items-center group-hover:font-bold" style={{ fontFamily: '3MCircularTT', color: textColor }}>
                      <MoreHorizontal
                        size={24}
                        className="transition-all group-hover:font-bold"
                        strokeWidth={showAllApps || chatModeState.ragChatType !== null ? 2.3 : 2}
                        style={{
                          color: showAllApps || chatModeState.ragChatType !== null ? '#ffffff' : textColor,
                          filter: showAllApps || chatModeState.ragChatType !== null ? `drop-shadow(0 0 12px rgba(255,255,255,1.0)) drop-shadow(0 0 40px ${accentColor})` : 'none',
                        }}
                      />
                      <p
                        className="text-xs pt-[.5px] font-medium transition-all group-hover:font-bold text-center"
                        style={{
                          fontFamily: '3MCircularTT',
                          color: showAllApps || chatModeState.ragChatType !== null ? '#ffffff' : textColor,
                          fontWeight: showAllApps || chatModeState.ragChatType !== null ? 900 : 500
                        }}
                      >
                        All Apps
                      </p>
                    </div>

                    {/* All Apps popup panel — rendered via portal to escape stacking contexts */}
                    {showAllApps && allAppsPopupPos && ReactDOM.createPortal(
                      <div
                        id="all-apps-portal"
                        onMouseEnter={() => {
                          if (allAppsCloseTimer.current) {
                            clearTimeout(allAppsCloseTimer.current);
                            allAppsCloseTimer.current = null;
                          }
                        }}
                        onMouseLeave={() => {
                          if (!allAppsLockedRef.current) {
                            allAppsCloseTimer.current = setTimeout(() => setShowAllApps(false), 250);
                          }
                        }}
                        onClick={() => { allAppsLockedRef.current = true; }}
                        style={{
                          position: 'fixed',
                          top: allAppsPopupPos.top,
                          left: allAppsPopupPos.left,
                          zIndex: 999999,
                          backgroundColor: 'var(--color-neutral-gray)',
                          border: `1px solid rgba(255,255,255,0.12)`,
                          boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)`,
                          minWidth: 'fit-content',
                          maxHeight: `calc(100vh - ${allAppsPopupPos.top}px - 16px)`,
                          borderRadius: '1rem',
                          overflow: 'hidden',
                          animation: 'slideInFromLeft 0.18s ease-out',
                        }}
                      >
                        {/* Inner div: scrollable content — scrollbar is clipped by parent's overflow:hidden + border-radius */}
                        <div
                          className="chat-page-scroll scrollbar-dark"
                          style={{
                            overflowY: 'auto',
                            maxHeight: `calc(100vh - ${allAppsPopupPos.top}px - 16px)`,
                            padding: '0.75rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                          }}
                        >
                        <p
                          className="text-xs font-semibold uppercase tracking-widest pb-1 border-b border-white/10 text-center"
                          style={{ fontFamily: '3MCircularTT', color: textColor, opacity: 0.5 }}
                        >
                          All Apps
                        </p>

                        <SearchInput
                          value={allAppsSearch}
                          onChange={setAllAppsSearch}
                          placeholder="Search apps..."
                          forceDark
                          containerClassName="w-full"
                          inputClassName="placeholder-style top-heavy-input"
                          iconSize={14}
                        />

                        {/* RAG app buttons — 3 per row grid */}
                        {(() => {
                          const filteredRags = ragItemsConfig.filter(r =>
                            r.label.toLowerCase().includes(allAppsSearch.toLowerCase()) ||
                            (r.shortLabel || '').toLowerCase().includes(allAppsSearch.toLowerCase())
                          );
                          if (filteredRags.length === 0) {
                            return (
                              <p
                                className="text-xs text-center py-3"
                                style={{ fontFamily: '3MCircularTT', color: '#6b7280' }}
                              >
                                No results
                              </p>
                            );
                          }
                          return (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 100px)', gap: '0.5rem' }}>
                        {filteredRags.map((rag) => {
                          const isActive = chatModeState.ragChatType === rag.key;
                          const displayLabel = rag.shortLabel || rag.label;
                          return (
                            <div
                              key={rag.key}
                              onClick={() => { allAppsLockedRef.current = false; handleRagChatSelect(rag.key); setShowAllApps(false); }}
                              className="relative overflow-visible rounded-2xl pt-[8px] pb-[8px] flex flex-col items-center justify-center group cursor-pointer transform hover:scale-105 hover:-translate-y-1 active:scale-95"
                              style={{
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                background: isActive
                                  ? `radial-gradient(circle at center, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 100%)`
                                  : 'radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)',
                                boxShadow: isActive
                                  ? `0 0 0 4px ${accent}80, 0 0 35px rgba(255,255,255,0.7), 0 4px 12px rgba(0,0,0,0.4), 0 8px 20px rgba(0,0,0,0.3), inset 0 0 20px rgba(255,255,255,0.2), inset 0 2px 6px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.3)`
                                  : `0 1px 2px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.1)`,
                                transition: 'transform 0.8s cubic-bezier(0.11, 0, 0.3, 0), all 0.2s ease'
                              }}
                            >
                              <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ background: isActive ? `linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 20%, ${accentColor} 40%, ${accentColor} 60%, rgba(255,255,255,0.9) 80%, rgba(255,255,255,1) 100%)` : 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.25) 80%, rgba(255,255,255,0.6) 100%)', mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', maskComposite: 'xor', WebkitMaskComposite: 'xor', padding: isActive ? '2.5px' : '1.5px', zIndex: -1 }} />
                              <div className="z-10 flex-col rounded transition flex items-center group-hover:font-bold" style={{ fontFamily: '3MCircularTT', color: textColor }}>
                                {React.createElement(rag.iconComponent as any, {
                                      size: 24,
                                      className: 'transition-all group-hover:font-bold',
                                      style: { color: isActive ? '#ffffff' : textColor, filter: isActive ? `drop-shadow(0 0 12px rgba(255,255,255,1.0)) drop-shadow(0 0 40px ${accentColor})` : 'none' }
                                    })
                                }
                                <div style={{ minHeight: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                  <p
                                    className="text-xs pt-[.5px] px-1 font-medium transition-all group-hover:font-bold text-center"
                                    style={{ fontFamily: '3MCircularTT', color: isActive ? '#ffffff' : textColor, fontWeight: isActive ? 900 : 500, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis', wordBreak: 'normal', whiteSpace: 'pre-line' }}
                                  >
                                    {displayLabel}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        </div>
                          );
                        })()}
                        </div>
                      </div>,
                      document.body
                    )}
                  </li>
                </React.Fragment>
              )}

              {/* New Chat Button — only shown on chat page when a RAG is active */}
              {currentPage === 'chat' && chatModeState.ragChatType !== null && (
                <li
                  className={`relative overflow-visible rounded-2xl pt-[8px] pb-[8px] pl-[2px] pr-[2px] flex flex-col items-center justify-center group ${chatModeState.isStreaming ? 'cursor-not-allowed opacity-50' : 'cursor-pointer transform hover:scale-105 hover:-translate-y-1 active:scale-95'}`}

                  onClick={() => !chatModeState.isStreaming && handleRagChatSelect(chatModeState.ragChatType as 'Everyday Insights' | 'Legal' | 'DevOps' | 'Reporting Portal' | 'TEBG' | 'PPMO' | 'Travel & Expenses' | 'ITSM' | 'JIRA')}
                  title={`New Chat — ${chatModeState.ragChatType}`}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    background: 'radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)',
                    boxShadow: `0 1px 2px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.1)`,
                    transition: 'transform 0.8s cubic-bezier(0.11, 0, 0.3, 0), all 0.2s ease'
                  }}
                >
                  {/* Gradient border */}
                  <div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.25) 80%, rgba(255,255,255,0.6) 100%)',
                      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      maskComposite: 'xor',
                      WebkitMaskComposite: 'xor',
                      padding: '1.5px',
                      zIndex: -1,
                    }}
                  />
                  <div className="z-10 flex-col block rounded transition flex items-center group-hover:font-bold" style={{ fontFamily: '3MCircularTT', color: textColor }}>
                    {/* Chat bubble with plus icon */}
                    <svg width="24" height="24" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-all group-hover:font-bold" style={{ color: textColor }}>
                      <path d="M14.037 16.8824V9.11765M10.1677 13H17.9063M14.037 24C20.0917 24 25 19.0751 25 13C25 6.92487 20.0917 2 14.037 2C7.98229 2 3.07397 6.92487 3.07397 13C3.07397 14.2294 3.27498 15.4117 3.64583 16.5156C3.78537 16.931 3.85515 17.1387 3.86774 17.2983C3.88017 17.4559 3.87077 17.5664 3.83192 17.7195C3.79257 17.8747 3.7057 18.0361 3.53197 18.3587L1.42235 22.2768C1.12143 22.8356 0.970969 23.115 1.00465 23.3307C1.03397 23.5185 1.14416 23.6838 1.30593 23.7828C1.49166 23.8966 1.80652 23.8639 2.43625 23.7986L9.04117 23.1135C9.24119 23.0928 9.3412 23.0825 9.43236 23.086C9.52201 23.0893 9.5853 23.0979 9.67274 23.1181C9.76163 23.1386 9.87341 23.1819 10.097 23.2682C11.3197 23.7409 12.6482 24 14.037 24Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p className="text-xs pt-[.5px] font-medium transition-all group-hover:font-bold text-center" style={{ fontFamily: '3MCircularTT', color: textColor, fontWeight: 500 }}>New Chat</p>
                  </div>
                </li>
              )}
            </ul>
            
            {/* Bottom section with settings */}
            <div className="mt-auto pt-8">
              <div className="space-y-2">
                {/* Icon to open Prompt library */}
                <button 
                  data-tutorial="prompt-library-button"
                  className="w-full flex items-center p-2 rounded transition hover:font-bold hover:bg-[#202024]"
                  onClick={onPromptLibraryClick}
                  style={{ 
                    color: isPromptLibraryOpen ? '#3b82f6' : textColor,
                    justifyContent: width > 120 ? 'flex-start' : 'center',
                    gap: width > 120 ? '0.5rem' : '0'
                  }}>
                  <svg width="24" height="24" viewBox="0 0 28 28"xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0" style={{ color: isPromptLibraryOpen ? '#3b82f6' : textColor }}  fill="currentColor" fillRule="evenodd" clipRule="evenodd">
                  <path d="M14.7979 27.5772C14.5386 27.8735 14.2207 28.0525 13.8379 27.9846C13.5447 27.9321 13.3718 27.7685 13.1836 27.5525L10.3098 24.2654H5.31231L4.67027 24.1852C3.94488 24.0617 3.28123 23.8148 2.66387 23.4321C1.12975 22.4846 0.163598 20.892 0 19.0833V5.17901C0 5.00926 0.0463014 4.83951 0.0709955 4.69136C0.453754 2.33333 2.28729 0.487654 4.6394 0.0771605C4.79374 0.0493827 4.96968 0 5.14563 0H22.8513L23.339 0.0740741C25.6973 0.472222 27.5432 2.33025 27.9259 4.69136L27.9969 5.1821V19.0864C27.8302 20.8981 26.8641 22.5 25.3145 23.4444C24.5397 23.9167 23.66 24.2191 22.7309 24.25L17.7149 24.2716L17.4525 24.537L14.7979 27.5802V27.5772ZM14.0015 25.6389L16.5018 22.784C16.6685 22.5833 16.8568 22.4475 17.1284 22.3951L22.7432 22.3858L23.1476 22.3148C24.8885 21.963 26.1356 20.429 26.1356 18.6512V5.61728C26.1356 4.53704 25.688 3.51235 24.8669 2.79938C24.265 2.27778 23.4594 1.87037 22.6229 1.87037H5.38022C4.59927 1.87037 3.86154 2.24074 3.26888 2.6821C3.04972 2.84568 2.89229 3.02469 2.72252 3.22531C2.15765 3.88889 1.8644 4.73765 1.8644 5.61728V18.7099C1.87058 20.4599 3.13306 21.9537 4.82769 22.3148L5.25675 22.3889L10.8716 22.3951C11.1556 22.4568 11.3315 22.5926 11.5136 22.7963L14.0046 25.6389H14.0015ZM13.0971 17.9568L12.631 15.9228C12.5199 15.4414 12.3779 14.9907 12.0723 14.5957C11.668 14.0741 11.1309 13.7099 10.4796 13.5586L8.16448 13.0278C7.74468 12.9321 7.45761 12.534 7.4607 12.1204C7.46379 11.7068 7.75394 11.3179 8.17374 11.2222L10.4456 10.7006C11.492 10.4599 12.3223 9.66358 12.56 8.61111L13.0354 6.49383C13.1403 6.03086 13.4058 5.64506 13.912 5.59568C14.4707 5.54012 14.8381 5.9537 14.9523 6.47531L15.4307 8.60494C15.69 9.76543 16.6284 10.5463 17.7705 10.7562L19.7954 11.2099C20.2275 11.3056 20.527 11.6914 20.53 12.1142C20.5331 12.5617 20.2245 12.9383 19.78 13.037L17.5112 13.5556C16.3506 13.821 15.5758 14.7562 15.3597 15.8981L14.8998 17.9414C14.8041 18.3642 14.4306 18.6451 14.0201 18.6574C13.6095 18.6698 13.1928 18.3951 13.094 17.9537L13.0971 17.9568ZM14.0756 14.0278C14.517 13.2377 15.1498 12.6049 15.94 12.1821L15.9832 12.1173C15.9832 12.1173 15.9493 12.0802 15.9277 12.0679C15.1375 11.6296 14.4831 10.9846 14.054 10.1914C14.0417 10.1667 14.017 10.1358 14.0015 10.1389C13.9861 10.142 13.9614 10.1667 13.9491 10.1914C13.5169 10.9877 12.8687 11.6327 12.0754 12.0679C12.0476 12.0833 12.0168 12.1019 12.0229 12.1173L12.0476 12.1728C12.8502 12.5988 13.4984 13.2407 13.9336 14.0401L14.0015 14.1049C14.017 14.1204 14.0633 14.0586 14.0756 14.0278Z"/>
                  </svg>
                  {width > 120 && (
                      <span 
                        style={{ 
                          fontFamily: '3MCircularTT',
                          fontSize: width > 140 ? '14px' : '12px',
                          transition: 'all 0.2s ease',
                          lineHeight: '1',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        Prompt Library
                      </span>
                  )}
                </button>
                {/* What's New */}
                {onShowWhatsNew && (
                  <button 
                    onClick={onShowWhatsNew}
                    className="w-full flex items-center p-2 rounded transition hover:font-bold hover:bg-[#202024]"
                    style={{ 
                      color: textColor,
                      justifyContent: width > 120 ? 'flex-start' : 'center',
                      gap: width > 120 ? '0.5rem' : '0'
                    }}
                    title="View what's new and take the guided tour"
                  >
                    <svg width="24" height="24" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                      <path d="M7.49991 0.876892C3.84222 0.876892 0.877075 3.84204 0.877075 7.49972C0.877075 11.1574 3.84222 14.1226 7.49991 14.1226C11.1576 14.1226 14.1227 11.1574 14.1227 7.49972C14.1227 3.84204 11.1576 0.876892 7.49991 0.876892ZM1.82707 7.49972C1.82707 4.36671 4.36689 1.82689 7.49991 1.82689C10.6329 1.82689 13.1727 4.36671 13.1727 7.49972C13.1727 10.6327 10.6329 13.1726 7.49991 13.1726C4.36689 13.1726 1.82707 10.6327 1.82707 7.49972ZM8.24992 4.49999C8.24992 4.9142 7.91413 5.24999 7.49992 5.24999C7.08571 5.24999 6.74992 4.9142 6.74992 4.49999C6.74992 4.08577 7.08571 3.74999 7.49992 3.74999C7.91413 3.74999 8.24992 4.08577 8.24992 4.49999ZM6.00003 5.99999H6.50003H7.50003C7.77618 5.99999 8.00003 6.22384 8.00003 6.49999V9.99999H8.50003H9.00003V11H8.50003H7.50003H6.50003H6.00003V9.99999H6.50003H7.00003V6.99999H6.50003H6.00003V5.99999Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                    </svg>
                    {width > 120 && (
                      <span 
                        style={{ 
                          fontFamily: '3MCircularTT',
                          fontSize: width > 140 ? '14px' : '12px',
                          transition: 'all 0.2s ease',
                          lineHeight: '1',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        What's New
                      </span>
                    )}
                  </button>
                )}
                
                {/* Clear Cache - development only */}
                {import.meta.env.VITE_ENV === 'development' && (
                  <button 
                    onClick={handleClearCache}
                    className="w-full flex items-center p-2 rounded transition hover:font-bold hover:bg-[#202024]"
                    style={{ 
                      color: textColor,
                      justifyContent: width > 120 ? 'flex-start' : 'center',
                      gap: width > 120 ? '0.5rem' : '0'
                    }}
                    title="Clear browser cache, localStorage, and sessionStorage (dev only)"
                  >
                    <ResetIcon width={24} height={24} className="flex-shrink-0" />
                    {width > 120 && (
                      <span 
                        style={{ 
                          fontFamily: '3MCircularTT',
                          fontSize: width > 140 ? '14px' : '12px',
                          transition: 'all 0.2s ease',
                          lineHeight: '1',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        Clear Cache
                      </span>
                    )}
                  </button>
                )}
                
                {/* Feedback */}
                <button 
                  onClick={handleFeedback}
                  className="w-full flex items-center p-2 rounded transition hover:font-bold hover:bg-[#202024]"
                  style={{ 
                    color: textColor,
                    justifyContent: width > 120 ? 'flex-start' : 'center',
                    gap: width > 120 ? '0.5rem' : '0'
                  }}
                  title="Send feedback about the application"
                >
                  <ChatBubbleIcon width={24} height={24} className="flex-shrink-0" />
                  {width > 120 && (
                    <span 
                      style={{ 
                        fontFamily: '3MCircularTT',
                        fontSize: width > 140 ? '14px' : '12px',
                        transition: 'all 0.2s ease',
                        lineHeight: '1',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      Feedback
                    </span>
                  )}
                </button>
                
                {/* Clear Chat */}
                {/* <button 
                  onClick={handleClearChat}
                  disabled={currentPage === 'main'}
                  className={`w-full flex items-center p-2 rounded transition ${
                    currentPage === 'main' 
                      ? 'opacity-40 cursor-not-allowed' 
                      : 'hover:font-bold hover:bg-[#202024]'
                  }`}
                  style={{ 
                    color: textColor,
                    justifyContent: width > 120 ? 'flex-start' : 'center',
                    gap: width > 120 ? '0.5rem' : '0'
                  }}
                  title={currentPage === 'main' ? 'Clear chat (only available in chat view)' : 'Clear the current chat conversation'}
                >
                  <TrashIcon width={24} height={24} className="flex-shrink-0" />
                  {width > 120 && (
                    <span 
                      style={{ 
                        fontFamily: '3MCircularTT',
                        fontSize: width > 140 ? '14px' : '12px',
                        transition: 'all 0.2s ease',
                        lineHeight: '1',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      Clear Chat
                    </span>
                  )}
                </button> */}
                
                {/* Logout */}
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center p-2 rounded transition hover:text-red-500 hover:bg-red-500/10 hover:font-bold"
                  style={{ 
                    color: textColor,
                    justifyContent: width > 120 ? 'flex-start' : 'center',
                    gap: width > 120 ? '0.5rem' : '0'
                  }}
                  title="Sign out of the application"
                >
                  <ExitIcon width={24} height={24} className="flex-shrink-0" />
                  {width > 120 && (
                    <span 
                      style={{ 
                        fontFamily: '3MCircularTT',
                        fontSize: width > 140 ? '14px' : '12px',
                        transition: 'all 0.2s ease',
                        lineHeight: '1',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      Logout
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </nav>
      )}
      {/* Persistent handle to reopen menu */}
      {collapsed && (
        <button
          className="fixed left-0 top-1/2 -translate-y-1/2 z-40 bg-[var(--dark-color-accent)] text-[var(--dark-color-text)] rounded-r px-2 py-4 shadow-md hover:bg-[var(--color-accent-hover)] transition"
          style={{ minWidth: '16px' }}
          // Reopen to the medium initial width (not max) per requirement
          onClick={() => { setCollapsed(false); saveWidthToStorage(initialWidth); }}
          title="Expand menu"
        >
          <span className="block text-lg">&#8250;</span>
        </button>
      )}
      
      {/* Feedback Dialog */}
      <FeedbackDialog showFeedback={showFeedback} setShowFeedback={setShowFeedback} />
    </>
  );
}